import {Card, DatePicker, Radio, Select} from 'antd';
import {CardGridProps} from 'antd/es/card';
import {RangePickerProps} from 'antd/es/date-picker';
import {
  Currencies,
  Interval,
  useChargesDateRangeKpiQuery,
  useOnUpdateAnalyticsSubscription
} from 'api/generated';
import {Stack} from 'components';
import moment, {Moment} from 'moment';
import {EventValue} from 'rc-picker/es/interface';
import React, {useState} from 'react';
import {ReportChart} from 'routes/Dashboard/ReportChart';
import {useDebouncedCallback} from 'use-debounce';
import {FilterByValue} from './Dashboard';

type Props = {
  accountId?: string;
  selectedCurrency: Currencies;
} & FilterByValue;

type RangePickerValue = [EventValue<Moment>, EventValue<Moment>] | null;

const {RangePicker} = DatePicker;
type ChartRow = {
  timestamp: number;
  first?: number;
  second?: number;
};
const chartHeight = 220;
const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const cardGridProps: CardGridProps = {
  style: {width: '50%'},
  hoverable: false
};
const getInterval = (diff: number) => {
  if (diff < 3) return Interval.HOUR;
  if (diff > 180) return Interval.MONTH;
  return Interval.DAY;
};
const getTimestampFormat = (internal: Interval) => {
  if (internal === Interval.HOUR) return 'LT';
  return 'll';
};

export const ReportsOverview: React.FC<Props> = ({
  accountId,
  filterBy,
  filterValue,
  selectedCurrency
}) => {
  const [firstRange, setFirstRange] = useState([
    moment().subtract(30, 'days'),
    moment().subtract(1, 'day')
  ]);
  const [secondRange, setSecondRange] = useState([
    moment().subtract(61, 'days'),
    moment().subtract(31, 'days')
  ]);
  const [interval, setInterval] = useState<Interval>(Interval.DAY);
  const rangeDiff = firstRange[1].diff(firstRange[0], 'days');

  const firstVariables = {
    start: firstRange[0].clone().startOf('day').unix(),
    end: firstRange[1].clone().endOf('day').unix(),
    accountId,
    [filterBy as string]: filterValue,
    currency: selectedCurrency,
    interval,
    timezone
  };
  const secondVariables = {
    start: secondRange[0].clone().startOf('day').unix(),
    end: secondRange[1].clone().endOf('day').unix(),
    accountId,
    [filterBy as string]: filterValue,
    currency: selectedCurrency,
    interval,
    timezone
  };
  const {
    data: firstCharges,
    loading: firstChargesLoading,
    refetch
  } = useChargesDateRangeKpiQuery({
    variables: firstVariables,
    fetchPolicy: 'network-only'
  });
  const {data: secondCharges, loading: secondChargesLoading} = useChargesDateRangeKpiQuery({
    variables: secondVariables
  });
  const update = useDebouncedCallback(refetch, 5000);
  useOnUpdateAnalyticsSubscription({
    onSubscriptionData: () => update()
  });
  const rangePickerProps: RangePickerProps = {
    style: {width: 230},
    format: 'L',
    allowClear: false,
    size: 'small',
    disabledDate: (date) => (date ? date.isAfter(moment().endOf('day')) : false)
  };
  const loading = firstChargesLoading || secondChargesLoading;
  const succeededAmountData: ChartRow[] = [];
  const succeededCountData: ChartRow[] = [];
  const failedCountData: ChartRow[] = [];
  const refundedCountData: ChartRow[] = [];
  const refundedAmountData: ChartRow[] = [];
  firstCharges?.chargesDateRangeKPI.data.forEach((row) => {
    if (interval === Interval.DAY && row.timestamp > moment().endOf('day').unix()) return;
    succeededAmountData.push({
      timestamp: row.timestamp,
      first: row.timestamp < moment().unix() ? row.succeededAmount : undefined
    });
    succeededCountData.push({
      timestamp: row.timestamp,
      first: row.timestamp < moment().unix() ? row.succeededCount : undefined
    });
    refundedAmountData.push({
      timestamp: row.timestamp,
      first: row.timestamp < moment().unix() ? row.refundedAmount : undefined
    });
    refundedCountData.push({
      timestamp: row.timestamp,
      first: row.timestamp < moment().unix() ? row.refundedCount : undefined
    });
    failedCountData.push({
      timestamp: row.timestamp,
      first: row.timestamp < moment().unix() ? row.failedCount : undefined
    });
  });
  secondCharges?.chargesDateRangeKPI.data.forEach((row, i) => {
    if (succeededAmountData[i]) {
      succeededAmountData[i].second = row.succeededAmount;
    }
    if (succeededCountData[i]) {
      succeededCountData[i].second = row.succeededCount;
    }
    if (failedCountData[i]) {
      failedCountData[i].second = row.failedCount;
    }
    if (refundedAmountData[i]) {
      refundedAmountData[i].second = row.refundedAmount;
    }
    if (refundedCountData[i]) {
      refundedCountData[i].second = row.refundedCount;
    }
  });
  const currency = new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: firstCharges?.chargesDateRangeKPI.currency ?? 'EUR'
  });
  const handleFirstRangeChange = (value: RangePickerValue) => {
    if (!value) return;
    const [start, end] = value;
    if (!start || !end) return;
    const diff = end.diff(start, 'days');
    setFirstRange([start, end]);
    const secondEnd = start.clone().subtract(1, 'day');
    setSecondRange([secondEnd.clone().subtract(diff, 'days'), secondEnd]);
    setInterval(getInterval(diff));
  };
  const handleSecondRangeChange = (value: RangePickerValue) => {
    if (!value) return;
    const [start, end] = value;
    if (!start || !end) return;
    const diff = firstRange[1].diff(firstRange[0], 'days');
    setSecondRange([start, start.clone().add(diff, 'days')]);
    setInterval(getInterval(diff));
  };
  const handleIntervalChange = (interval: Interval) => {
    setInterval(interval);
  };
  return (
    <Card
      title="Reports overview"
      size="small"
      bodyStyle={{backgroundColor: '#f7fafc'}}
      extra={
        <Stack gutter={10}>
          <RangePicker
            onChange={handleFirstRangeChange}
            value={firstRange as RangePickerValue}
            ranges={{
              Today: [moment().startOf('day'), moment().endOf('day')],
              'Last 7 days': [moment().subtract(7, 'days'), moment()],
              'Last 4 weeks': [moment().subtract(1, 'month'), moment()],
              'Last 3 months': [moment().subtract(3, 'months'), moment()],
              'Last 12 months': [moment().subtract(12, 'months'), moment()],
              'Month to date': [moment().startOf('month'), moment()],
              'Quarter to date': [moment().startOf('quarter'), moment()],
              'Year to date': [moment().startOf('year'), moment()]
            }}
            {...rangePickerProps}
          />
          <span>vs</span>
          <RangePicker
            onChange={handleSecondRangeChange}
            value={secondRange as RangePickerValue}
            {...rangePickerProps}
          />
          <Radio.Group
            value={interval}
            onChange={(e) => handleIntervalChange(e.target.value)}
            size="small">
            {rangeDiff < 4 && <Radio.Button value={Interval.HOUR}>Hourly</Radio.Button>}
            {rangeDiff > 1 && <Radio.Button value={Interval.DAY}>Daily</Radio.Button>}
            {rangeDiff > 14 && <Radio.Button value={Interval.WEEK}>Weekly</Radio.Button>}
            {rangeDiff > 62 && <Radio.Button value={Interval.MONTH}>Monthly</Radio.Button>}
            {rangeDiff > 730 && <Radio.Button value={Interval.QUARTER}>Yearly</Radio.Button>}
          </Radio.Group>
        </Stack>
      }>
      <Card.Grid {...cardGridProps}>
        <ReportChart
          loading={loading}
          height={chartHeight}
          data={succeededAmountData}
          syncId="successful"
          dataLabel="Gross volume"
          total={{
            first: firstCharges?.chargesDateRangeKPI.total.succeededAmount ?? 0,
            second: secondCharges?.chargesDateRangeKPI.total.succeededAmount ?? 0
          }}
          formatTimestamp={(value) => moment.unix(value).format(getTimestampFormat(interval))}
          formatValue={(value) => currency.format(value / 100)}
        />
      </Card.Grid>
      <Card.Grid {...cardGridProps}>
        <ReportChart
          loading={loading}
          height={chartHeight}
          data={refundedAmountData}
          negative
          syncId="refunds"
          dataLabel="Refunded volume"
          total={{
            first: firstCharges?.chargesDateRangeKPI.total.refundedAmount ?? 0,
            second: secondCharges?.chargesDateRangeKPI.total.refundedAmount ?? 0
          }}
          formatTimestamp={(value) => moment.unix(value).format(getTimestampFormat(interval))}
          formatValue={(value) => currency.format(value / 100)}
        />
      </Card.Grid>
      <Card.Grid {...cardGridProps}>
        <ReportChart
          loading={loading}
          height={chartHeight}
          data={succeededCountData}
          syncId="successful"
          dataLabel="Successful payments"
          total={{
            first: firstCharges?.chargesDateRangeKPI.total.succeededCount ?? 0,
            second: secondCharges?.chargesDateRangeKPI.total.succeededCount ?? 0
          }}
          formatTimestamp={(value) => moment.unix(value).format(getTimestampFormat(interval))}
          formatValue={(value) => value.toLocaleString()}
        />
      </Card.Grid>
      <Card.Grid {...cardGridProps}>
        <ReportChart
          loading={loading}
          height={chartHeight}
          data={refundedCountData}
          negative
          syncId="refunds"
          dataLabel="Refunded payments"
          total={{
            first: firstCharges?.chargesDateRangeKPI.total.refundedCount ?? 0,
            second: secondCharges?.chargesDateRangeKPI.total.refundedCount ?? 0
          }}
          formatTimestamp={(value) => moment.unix(value).format(getTimestampFormat(interval))}
          formatValue={(value) => value.toLocaleString()}
        />
      </Card.Grid>
      <Card.Grid {...cardGridProps}>
        <ReportChart
          loading={loading}
          height={chartHeight}
          data={failedCountData}
          negative
          dataLabel="Failed payments"
          total={{
            first: firstCharges?.chargesDateRangeKPI.total.failedCount ?? 0,
            second: secondCharges?.chargesDateRangeKPI.total.failedCount ?? 0
          }}
          formatTimestamp={(value) => moment.unix(value).format(getTimestampFormat(interval))}
          formatValue={(value) => value.toLocaleString()}
        />
      </Card.Grid>
    </Card>
  );
};
